import * as React from 'react';
import { Button, notification } from 'antd';
import { capitalize } from 'lodash';

const DEFAULT_DURATION_SECONDS = 4;

/* Cap visible notifications so they don't stack into an unreadable column,
   and give them a little extra top breathing room. */
notification.config({
    placement: 'topRight',
    top: 72,
    maxCount: 1,
    duration: DEFAULT_DURATION_SECONDS,
});

export enum Notification {
    Success = 'success',
    Info = 'info',
    Warning = 'warning',
    Error = 'error',
}

export enum NotificationPlacement {
    TopRight = 'topRight',
    TopLeft = 'topLeft',
    BottomRight = 'bottomRight',
    BottomLeft = 'bottomLeft',
}

export interface NotificationOptions {
    type?: Notification;
    message?: string;
    text: string;
    duration?: number;
    placement?: NotificationPlacement;
    button?: JSX.Element;
}

export const notificationText = {
    Success: 'Success',
    Info: 'Please ensure the data you have entered is correct',
    Warning: 'Something went wrong',
    Error: 'Please update the data you have entered',
};

export const showNotification = ({
    message,
    text,
    duration,
    placement,
    button,
    type = Notification.Error,
}: NotificationOptions) => {
    /* Dedupe identical messages: hash the type + description as the notification
       key so firing the same error twice updates the visible notice rather than
       stacking a duplicate. */
    const dedupeKey = `${type}|${text}`;

    notification[type]({
        key: dedupeKey,
        message: message || capitalize(type),
        description: text,
        // `duration` is in seconds; `0` means "never auto-close" — only honour an
        // explicit 0 caller, otherwise fall back to the default.
        duration: duration === undefined ? DEFAULT_DURATION_SECONDS : duration,
        placement: placement || NotificationPlacement.TopRight,
        btn: button,
    });
};

interface UndoableOptions {
    /* Short message shown to the user, eg "Benefit archived." */
    text: string;
    /* Label for the undo button, defaults to "Undo". */
    undoLabel?: string;
    /* Called when the user clicks Undo. */
    onUndo: () => void;
    /* How long the notice stays visible before auto-dismissing (and the
       undo window closes). Defaults to 6s — slightly longer than the
       standard 4s so users have time to react. */
    duration?: number;
    /* Optional onExpire for parents that want to know the undo window
       expired without the user clicking (eg trigger the actual delete
       at this point instead of optimistically). */
    onExpire?: () => void;
}

/* Toast with an Undo button for reversible destructive actions
   (archive, soft-delete, etc). Pattern matches Gmail/Linear:
   - Optimistically remove the row.
   - showUndoableNotification({ text, onUndo }).
   - If the user clicks Undo, restore the row.
   - If it auto-closes, the destructive action stands. */
export const showUndoableNotification = ({
    text,
    undoLabel = 'Undo',
    onUndo,
    duration = 6,
    onExpire,
}: UndoableOptions) => {
    const dedupeKey = `undo|${text}|${Date.now()}`;
    let undone = false;

    const close = () => notification.close(dedupeKey);

    notification.success({
        key: dedupeKey,
        message: 'Done',
        description: text,
        duration,
        placement: NotificationPlacement.TopRight,
        btn: React.createElement(
            Button,
            {
                size: 'small',
                type: 'primary',
                onClick: () => {
                    undone = true;
                    onUndo();
                    close();
                },
            },
            undoLabel,
        ),
        onClose: () => {
            if (!undone && onExpire) {
                onExpire();
            }
        },
    });
};
